

import { useCallback, useMemo, useState } from 'react'
import { 
  useDeleteAssignmentMutation, 
  useGetEmployeesQuery as getEmployeesQuery, 
  useGetEmployeeQuery as getEmployeeQuery, 
  useGetCustomersQuery as getCustomersQuery, 
  useGetCustomerQuery as getCustomerQuery, 
  useGetCustomerQuery,
  useGetEmployeeQuery,
  useGetAssignmentsQuery} from '../../../api/plamo-api'
import { Link, useNavigate } from 'react-router-dom'
import { DatePicker, Modal } from 'antd'
import dayjs from 'dayjs'
import PlamoApiWithSaveNotifications from '../util/with-save-notification'
import { ActionTableState } from '../action-table/action-table'

const { useUpdateAssignmentWithSaveNotificationMutation } = PlamoApiWithSaveNotifications;

function AssignmentTable({ preselectedEmployeeId, preselectedCustomerId }: any) {
  const { data: assignments } = useGetAssignmentsQuery({ f: JSON.stringify({ "employee.id": preselectedEmployeeId ? { c: "EQ", v: preselectedEmployeeId } : undefined, "customer.id": preselectedCustomerId ? { c: "EQ", v: preselectedCustomerId } : undefined })}) as any
  const [deleteAssignment, { isLoading: isDeletingAssignment }] = useDeleteAssignmentMutation();
  const [updateAssignment] = useUpdateAssignmentWithSaveNotificationMutation();
  const [openUnassignmentDialog, setOpenUnassignmentDialog] = useState<any>(null);
  const navigate = useNavigate()
  const { data: unassignCustomer } = useGetCustomerQuery({ id: openUnassignmentDialog?.customerId }, { skip: !openUnassignmentDialog }) as any;
  const { data: unassignEmployee } = useGetEmployeeQuery({ id: openUnassignmentDialog?.employeeId }, { skip: !openUnassignmentDialog }) as any;
  const [unassignDate, setUnassignDate] = useState<any>(dayjs());

  const isLoading = isDeletingAssignment;
  
  const columns = useMemo(() => [
    {
      title: "Id",
      dataIndex: "id",
      sorter: true,
      filter: true,
      type: 'int',
    },
    ...(preselectedCustomerId ? [] : [
      {
        title: "Kunde",
        dataIndex: "customerId",
        type: 'reference',
        typeAttrs: {
          referenceList: () => getCustomersQuery({ o: 0, c: 1000, st: JSON.stringify({ name: 'asc' }), f: JSON.stringify({}) }),
          referenceSingle: id => getCustomerQuery({ id }),
          referenceDisplayString: customer => `${customer.firstName} ${customer.lastName}`,
          referenceLabel: customer => <Link to={`/customer/${customer.id}`}>{customer.firstName} {customer.lastName}</Link>,
          referenceGetId: customer => customer.id
        },   
      },
    ]),
    ...(preselectedEmployeeId ? [] : [
      {
        title: "Mitarbeiter",
        dataIndex: "employeeId",
        type: 'reference',
        typeAttrs: {
          referenceList: () => getEmployeesQuery({ o: 0, c: 1000, st: JSON.stringify({ name: 'asc' }), f: JSON.stringify({}) }),
          referenceSingle: id => getEmployeeQuery({ id }),
          referenceDisplayString: employee => `${employee.firstName} ${employee.lastName}`,
          referenceLabel: employee => <Link to={`/employee/${employee.id}`}>{employee.firstName} {employee.lastName}</Link>,
          referenceGetId: employee => employee.id
        },      
      },
    ]
    ),
    {
      title: "Beginn",
      dataIndex: "startDate",
      type: 'date',
    },
    {
      title: "Ende",
      dataIndex: "endDate",
      type: 'date',
    }
  ], [preselectedEmployeeId, preselectedCustomerId])

  const assignmentLinkQueryString = useMemo(() => {
    const resultMap = {
      preselectedEmployeeId,
      preselectedCustomerId,
    }
    const result = Object.entries(resultMap).filter(([_1, value]) => value).map(([key, value]) => `${key}=${value}`).join('&');
    return result ? `?${result}` : ''
  }, [preselectedEmployeeId, preselectedCustomerId])

  const unassign = useCallback(async () => {
    const newItem = {...openUnassignmentDialog, endDate: unassignDate }
    console.log("Update item", newItem)
    await updateAssignment(newItem);
    setOpenUnassignmentDialog(false);
  }, [openUnassignmentDialog, unassignDate, updateAssignment, setOpenUnassignmentDialog])

  const currentAssignments = useMemo(() => assignments?.items?.filter(a => (!a.startDate || dayjs(a.startDate).valueOf() <= dayjs().valueOf()) && (!a.endDate || dayjs(a.endDate).add(1, 'day').valueOf() >= dayjs().valueOf())), [assignments?.items])
  const pastAssignments = useMemo(() => assignments?.items?.filter(a => a.endDate && dayjs(a.endDate).add(1, 'day').valueOf() <= dayjs().valueOf()), [assignments?.items])
  const futureAssignments = useMemo(() => assignments?.items?.filter(a => a.startDate && dayjs(a.startDate).valueOf() >= dayjs().valueOf()), [assignments?.items])

  return (
    <>
    <Modal
        title="Zuordnung aufheben"
        open={!!openUnassignmentDialog}
        onOk={unassign}
        cancelText="Abbrechen"
        onCancel={() => setOpenUnassignmentDialog(null)}
      >
        <><p>Bitte Datum angeben, ab wann die Zuordnung <strong>{unassignCustomer?.firstName} {unassignCustomer?.lastName}</strong> &ndash; <strong>{unassignEmployee?.firstName} {unassignEmployee?.lastName}</strong> aufgehoben werden soll:</p>
        <DatePicker value={unassignDate} onChange={setUnassignDate} format={'DD.MM.YYYY'} />
        </>
    </Modal>
    <h2>Aktuelle Zuordnungen</h2>
    <div>
    <ActionTableState
          isParentLoading={isLoading}
          buttonRow={{
            buttons: [
              {
                text: 'Neu',
                buttonType: 'primary',
                onClick: () => navigate(`./new${assignmentLinkQueryString}`)
              },
            ]
          }}
          tableData={{
            defaultSortKey: 'customer.id',
            columns,
            dataSource: {
              pageableQueryEndpoint: ({ f, ...rest }) => ({ data: { items: currentAssignments, pageSize: currentAssignments?.length, offset: 0 } }),
              queryResultMapping: (result) => result?.items
            },
            actions: [
              {
                caption: 'Bearbeiten',
                onClick: (item) => navigate(`./${item.id}${assignmentLinkQueryString}`)
              },
              {
                caption: 'Zuordnung aufheben',
                onClick: (item) => setOpenUnassignmentDialog(item)
              },
              {
                caption: 'Löschen',
                danger: true,
                onClick: deleteAssignment,
                confirmTitle: _ => "Zuordnung löschen?",
                confirmContent: _ => <>Soll die Zuordnung wirklich gelöscht werden?</>
              }
            ]
          }}
        />
    </div>
    { !!futureAssignments?.length && <>
      <h2>Künftige Zuordnungen</h2>
      <div>
      <ActionTableState
            isParentLoading={isLoading}
            tableData={{
              defaultSortKey: 'customer.id',
              columns,
              dataSource: {
                pageableQueryEndpoint: ({ f, ...rest }) => ({ data: { items: futureAssignments, pageSize: futureAssignments?.length, offset: 0 } }),
                queryResultMapping: (result) => result?.items
              },
              actions: [
                {
                  caption: 'Bearbeiten',
                  onClick: (item) => navigate(`./${item.id}${assignmentLinkQueryString}`)
                },
                {
                  caption: 'Löschen',
                  danger: true,
                  onClick: (item) => deleteAssignment(item),
                  confirmTitle: _ => "Zuordnung löschen?",
                  confirmContent: _ => <>Soll die Zuordnung wirklich gelöscht werden?</>
                  
                }
              ]
            }}
          />
        </div>
      </> }
    { !!pastAssignments?.length && <>
      <h2>Vergangene Zuordnungen</h2>
      <div>
      <ActionTableState
            isParentLoading={isLoading}
            tableData={{
              defaultSortKey: 'customer.id',
              columns,
              dataSource: {
                pageableQueryEndpoint: ({ f, ...rest }) => ({ data: { items: pastAssignments, pageSize: pastAssignments?.length, offset: 0 } }),
                queryResultMapping: (result) => result?.items
              },
              actions: [
                {
                  caption: 'Bearbeiten',
                  onClick: (item) => navigate(`./${item.id}${assignmentLinkQueryString}`)
                },
                {
                  caption: 'Löschen',
                  danger: true,
                  onClick: (item) => deleteAssignment(item),
                  confirmTitle: _ => "Zuordnung löschen?",
                  confirmContent: _ => <>Soll die Zuordnung wirklich gelöscht werden?</>
                  
                }
              ]
            }}
          />
        </div>
      </> }
    </>
    
  )
}

export default AssignmentTable